/**
 * Privacy-hardened PostHog analytics and client diagnostics integration.
 *
 * Invariants:
 * 1. ZERO CODE TELEMETRY: every surface that can show user code (editors,
 *    highlight overlays, gutters, search bars, scratchpad, console, preview,
 *    share dialog) is blocked from session replay, all other replay text is
 *    masked, and event properties are sanitised recursively.
 * 2. NO DEVICE STORAGE: PostHog runs with in-memory persistence, so it sets no
 *    cookie and writes nothing to localStorage; every page load is a new
 *    anonymous visitor. Browsers sending Do Not Track are not captured.
 * 3. EU ONLY: data is sent to PostHog's EU region. Any other host is ignored.
 * 4. Non-blocking async initialization: Initialized in an idle phase so startup
 *    and rendering are never delayed.
 * 5. Graceful degradation: If no API key is configured (local dev, airgapped,
 *    or omitted), all operations safely no-op without console spam.
 * 6. Failsafe wrappers: Exceptions in analytics calls are caught locally and
 *    never crash the playground interface.
 */

export const EU_POSTHOG_HOST = 'https://eu.i.posthog.com';

const ANALYTICS_CONFIG = {
  "apiKey": "phc_vUzxkcXaqW9otz65uAvUeA7FvepG2ac86d5zq9bPGUCF",
  "apiHost": "https://eu.i.posthog.com"
};

// Surfaces that can contain user code or user-authored text. Blocked elements
// are replaced by an empty placeholder in session replay.
export const REPLAY_BLOCK_SELECTOR = [
  '.editor-wrapper',
  '.editor-textarea',
  '.highlight-overlay',
  '.line-number-gutter',
  '.editor-search-bar',
  '.inspect-line-marker',
  '#panel-scratchpad',
  '#scratch-editor',
  '#console-output',
  '#preview-iframe',
  '#share-modal'
].join(', ');

const FORBIDDEN_PROPERTY_KEYS = new Set(['code', 'html', 'css', 'js', 'script', 'content', 'snippet', 'text', 'value', 'payload']);
const MAX_SANITIZE_DEPTH = 6;

let posthogInstance = null;
let initialized = false;
let initAttempted = false;
// Events raised before the idle-phase init (startup failures, early sandbox
// timeouts) are held here and flushed once PostHog is configured.
const MAX_PENDING_EVENTS = 20;
let pendingEvents = [];

function normalizeHost(host) {
  return typeof host === 'string' ? host.trim().replace(/\/+$/, '') : '';
}

export function getAnalyticsConfig() {
  const apiKey = (typeof window !== 'undefined' && window.__POSTHOG_API_KEY__) || ANALYTICS_CONFIG.apiKey;
  const requestedHost = normalizeHost((typeof window !== 'undefined' && window.__POSTHOG_HOST__) || ANALYTICS_CONFIG.apiHost);
  if (requestedHost && requestedHost !== EU_POSTHOG_HOST) {
    // The privacy policy promises EU processing: never send anywhere else.
    return { apiKey, apiHost: EU_POSTHOG_HOST, rejectedHost: requestedHost };
  }
  return { apiKey, apiHost: EU_POSTHOG_HOST };
}

export function isAnalyticsActive() {
  return Boolean(initialized && posthogInstance);
}

function sanitizeValue(value, depth) {
  if (Array.isArray(value)) {
    return depth >= MAX_SANITIZE_DEPTH ? [] : value.map((item) => sanitizeValue(item, depth + 1));
  }
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    if (depth >= MAX_SANITIZE_DEPTH) return {};
    const sanitized = {};
    for (const [key, item] of Object.entries(value)) {
      // Guardrail: Never allow user code, markup, or snippets in event properties
      if (FORBIDDEN_PROPERTY_KEYS.has(key)) continue;
      sanitized[key] = sanitizeValue(item, depth + 1);
    }
    return sanitized;
  }
  return value;
}

export function sanitizeAnalyticsProperties(properties) {
  if (!properties || typeof properties !== 'object') {
    return properties || {};
  }
  return sanitizeValue(properties, 0);
}

export function buildPostHogOptions(apiHost) {
  return {
    api_host: apiHost,
    persistence: 'memory',
    respect_dnt: true,
    autocapture: true,
    capture_pageview: true,
    capture_exceptions: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: REPLAY_BLOCK_SELECTOR
    },
    sanitize_properties: (properties) => sanitizeAnalyticsProperties(properties)
  };
}

export function initAnalytics() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  if (initialized) {
    return posthogInstance;
  }

  const { apiKey, apiHost } = getAnalyticsConfig();
  initAttempted = true;
  if (!apiKey) {
    pendingEvents = [];
    return null;
  }

  try {
    // PostHog snippet loader with privacy guardrails
    (function(t, e) {
      var o, n, p, r;
      if (!e.__SV) {
        window.posthog = e;
        e._i = [];
        e.init = function(i, s, a) {
          function g(t, e) {
            var o = e.split('.');
            if (o.length === 2) {
              t = t[o[0]];
              e = o[1];
            }
            t[e] = function() {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
          }
          p = t.createElement('script');
          p.type = 'text/javascript';
          p.crossOrigin = 'anonymous';
          p.async = true;
          p.src = (s.api_host || EU_POSTHOG_HOST).replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
          r = t.getElementsByTagName('script')[0];
          if (r && r.parentNode) {
            r.parentNode.insertBefore(p, r);
          } else {
            (document.head || document.documentElement).appendChild(p);
          }
          var u = e;
          if (typeof a !== 'undefined') {
            u = e[a] = [];
          } else {
            a = 'posthog';
          }
          u.people = u.people || [];
          u.toString = function(t) {
            var e = 'posthog';
            if (a !== 'posthog') e += '.' + a;
            if (!t) e += ' (stub)';
            return e;
          };
          u.people.toString = function() {
            return u.toString(1) + '.people (stub)';
          };
          o = ('init capture register register_once register_for_session unregister unregister_for_session ' +
            'getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment ' +
            'getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey ' +
            'canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups ' +
            'setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags ' +
            'resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url ' +
            'alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException ' +
            'loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing ' +
            'has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId').split(' ');
          for (n = 0; n < o.length; n++) g(u, o[n]);
          e._i.push([i, s, a]);
        };
        e.__SV = 1;
      }
    })(document, window.posthog || []);

    window.posthog.init(apiKey, buildPostHogOptions(apiHost));

    posthogInstance = window.posthog;
    initialized = true;
    flushPendingEvents();
    return posthogInstance;
  } catch (_) {
    return null;
  }
}

function queueUntilInitialized(entry) {
  if (initialized || initAttempted) return false;
  if (pendingEvents.length < MAX_PENDING_EVENTS) pendingEvents.push(entry);
  return true;
}

function flushPendingEvents() {
  const queued = pendingEvents;
  pendingEvents = [];
  for (const entry of queued) {
    if (entry.kind === 'error') captureAnalyticsError(entry.error, entry.context);
    else captureAnalyticsEvent(entry.eventName, entry.properties);
  }
}

export function captureAnalyticsEvent(eventName, properties = {}) {
  try {
    if (queueUntilInitialized({ kind: 'event', eventName, properties })) return;
    if (!initialized || !posthogInstance || typeof posthogInstance.capture !== 'function') {
      return;
    }
    posthogInstance.capture(eventName, sanitizeAnalyticsProperties(properties));
  } catch (_) {
    // Fail silently to never disrupt playground operation
  }
}

export function captureAnalyticsError(error, context = {}) {
  try {
    if (queueUntilInitialized({ kind: 'error', error, context })) return;
    if (!initialized || !posthogInstance) {
      return;
    }
    const message = error instanceof Error ? error.message : String(error || 'Unknown error');
    const sanitized = sanitizeAnalyticsProperties({
      error_message: message,
      error_name: error instanceof Error ? error.name : 'Error',
      ...context
    });
    if (typeof posthogInstance.captureException === 'function' && error instanceof Error) {
      posthogInstance.captureException(error, sanitized);
    } else if (typeof posthogInstance.capture === 'function') {
      posthogInstance.capture('$exception', sanitized);
    }
  } catch (_) {
    // Fail silently
  }
}

export function resetAnalyticsStateForTesting() {
  posthogInstance = null;
  initialized = false;
  initAttempted = false;
  pendingEvents = [];
  if (typeof window !== 'undefined') {
    delete window.posthog;
    delete window.__POSTHOG_API_KEY__;
    delete window.__POSTHOG_HOST__;
  }
}
